/**
 * Rentals API service
 * Manages rental vehicle catalog
 */

import { api } from './api';

export type FuelType = 'gasoline' | 'diesel' | 'hybrid' | 'electric';
export type TransmissionType = 'manual' | 'automatic';

// Available features for rental vehicles
export const RENTAL_FEATURES = [
  'bluetooth',
  'apple_carplay',
  'android_auto',
  'aux_usb',
  'cruise_control',
  'rear_camera',
  'isofix',
  'ac',
  'keyless',
  'all_season_tires',
] as const;

export type RentalFeature = (typeof RENTAL_FEATURES)[number];

export const FEATURE_LABELS: Record<RentalFeature, string> = {
  bluetooth: 'Bluetooth',
  apple_carplay: 'Apple CarPlay',
  android_auto: 'Android Auto',
  aux_usb: 'AUX/USB',
  cruise_control: 'Control de Velocidad',
  rear_camera: 'Camara de Vision Trasera',
  isofix: 'Fijaciones Isofix',
  ac: 'Aire Acondicionado',
  keyless: 'Sin Llaves',
  all_season_tires: 'Neumaticos Todas las Estaciones',
};

export const FUEL_TYPE_LABELS: Record<FuelType, string> = {
  gasoline: 'Gasolina',
  diesel: 'Diesel',
  hybrid: 'Hibrido',
  electric: 'Electrico',
};

export const TRANSMISSION_LABELS: Record<TransmissionType, string> = {
  manual: 'Manual',
  automatic: 'Automatico',
};

export interface RentalVehicle {
  id: string;
  registration: string; // Internal only - not exposed publicly
  make: string;
  model: string;
  year: number;
  dailyRate: number;
  status: 'available' | 'rented' | 'maintenance';
  description?: string | null;
  // Vehicle specs
  fuelType?: FuelType | null;
  transmission?: TransmissionType | null;
  seats?: number | null;
  // Features
  features?: string[] | null;
  // Multiple images
  imageUrls?: string[] | null;
  // External booking/referral link
  externalLink?: string | null;
  // Pickup location
  pickupLocation?: string | null;
  // Legacy fields
  photoR2Key?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateRentalVehicleRequest {
  registration: string;
  make: string;
  model: string;
  year: number;
  dailyRate: number;
  status?: 'available' | 'rented' | 'maintenance';
  description?: string;
  fuelType?: FuelType | null;
  transmission?: TransmissionType | null;
  seats?: number | null;
  features?: string[] | null;
  imageUrls?: string[] | null;
  externalLink?: string | null;
  pickupLocation?: string | null;
}

export interface UpdateRentalVehicleRequest {
  registration?: string;
  make?: string;
  model?: string;
  year?: number;
  dailyRate?: number;
  status?: 'available' | 'rented' | 'maintenance';
  description?: string | null;
  fuelType?: FuelType | null;
  transmission?: TransmissionType | null;
  seats?: number | null;
  features?: string[] | null;
  imageUrls?: string[] | null;
  externalLink?: string | null;
  pickupLocation?: string | null;
}

// Note: api.get/post/etc return the `data` property from the response directly
// So if API returns { success, data: [...], count }, we get just [...]

export const rentalsApi = {
  /**
   * Get public rental catalog (available vehicles only)
   * Returns array of vehicles directly
   */
  getPublicCatalog: () =>
    api.get<RentalVehicle[]>('/api/public/rentals'),

  /**
   * Get all rental vehicles (staff - all statuses)
   * Returns array of vehicles directly
   */
  list: () =>
    api.get<RentalVehicle[]>('/api/public/rentals/admin'),

  /**
   * Get rental vehicle by ID (staff)
   * Returns vehicle directly
   */
  getById: (id: string) =>
    api.get<RentalVehicle>(`/api/public/rentals/admin/${id}`),

  /**
   * Create new rental vehicle (admin only)
   */
  create: (data: CreateRentalVehicleRequest) =>
    api.post<RentalVehicle>('/api/public/rentals/admin', data),

  /**
   * Update rental vehicle (admin only)
   */
  update: (id: string, data: UpdateRentalVehicleRequest) =>
    api.patch<RentalVehicle>(`/api/public/rentals/admin/${id}`, data),

  /**
   * Delete rental vehicle (admin only)
   */
  delete: (id: string) =>
    api.delete<{ message: string }>(`/api/public/rentals/admin/${id}`),
};
